/*
  Bisou Memoire: Clothes rail (Creaeza)

  - Garments hang side-on on a rail. Each one is a small damped pendulum, so a breeze,
    scrolling, the pointer and neighbours pushing past all make it swing and settle calmly.
  - Desktop: hover turns a garment to the front, neighbours make room. Drag or arrows move the rail.
  - Phones: the garment in the middle of the rail turns as the rail is swiped. Tap to open.
  - Details panel: the garment flies from the rail into the panel, drag to turn it
    (front, side, back), colour swatches, Add to bag through the theme's cart drawer.
  - Filter tabs: garments leave, the rest slide along the rail, new ones drop onto it.
*/
(function () {
  'use strict';

  if (window.BisouClothesRail) return;

  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  var hoverMQ = window.matchMedia('(hover: hover) and (pointer: fine)');

  // Pendulum feel: stiffness and damping. Lower damping = more gentle swings.
  var K = 26;
  var C = 3.3;
  var SLIDE_KICK = 0.11;   // deg/s per px a garment is pushed along the rail
  var SCROLL_KICK = 0.8;   // deg/s per px the rail scrolls in one frame
  var BRUSH_KICK = 7;      // deg/s per px/ms of pointer speed
  var MAX_ANGLE = 9;

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function json(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  function smooth(w) { return w * w * (3 - 2 * w); }
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
  function resize(src, w) {
    if (!src) return '';
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&])width=\d+/, '$1width=' + w);
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  var uid = 0;

  function Rail(root) {
    var self = this;
    this.root = root;
    this.id = 'bmr' + (++uid);
    this.cfg = json(root.querySelector('[data-bmr-config]')) || {};
    this.text = this.cfg.text || {};
    this.reduced = reduceMQ.matches;
    this.sway = this.reduced ? 0 : clamp(Number(this.cfg.sway) || 0, 0, 10);
    this.viewport = root.querySelector('[data-bmr-viewport]');
    this.track = root.querySelector('[data-bmr-track]');
    this.prevBtn = root.querySelector('[data-bmr-prev]');
    this.nextBtn = root.querySelector('[data-bmr-next]');
    this.infoBody = root.querySelector('[data-bmr-info-body]');
    this.infoEyebrow = root.querySelector('[data-bmr-info-eyebrow]');
    this.infoTitle = root.querySelector('[data-bmr-info-title]');
    this.infoMeta = root.querySelector('[data-bmr-info-meta]');
    this.openBtn = root.querySelector('[data-bmr-open]');
    this.tabsEl = root.querySelector('[data-bmr-tabs]');
    this.tabLine = root.querySelector('[data-bmr-tab-line]');

    this.items = [].slice.call(root.querySelectorAll('[data-bmr-item]')).map(function (li, i) {
      var data = json(li.querySelector('[data-bmr-json]')) || {};
      data.images = data.images || {};
      return {
        i: i,
        li: li,
        slide: li.querySelector('.bmr-slide'),
        hanger: li.querySelector('[data-bmr-hanger]'),
        garment: li.querySelector('[data-bmr-garment]'),
        front: li.querySelector('.bmr-img--front'),
        group: li.getAttribute('data-group') || '',
        blockId: li.getAttribute('data-block-id'),
        data: data,
        theta: 0,
        omega: 0,
        phase: i * 0.62 + Math.random() * 0.5,
        written: null,
        x: 0,
        visible: true
      };
    });
    if (!this.items.length) return;

    this.visible = this.items.slice();
    this.filter = 'all';
    this.active = null;
    this.running = false;
    this.inView = false;
    this.paused = false;
    this.lastScroll = this.viewport.scrollLeft;
    this.lastTime = 0;
    this.listeners = [];

    this.measure();
    this.setMode();
    this.bind();
    this.updateTabs(false);
    this.roving(this.items[0]);
    var first = this.middle();
    if (first) this.updateInfo(first, true);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { self.moveTabLine(false); });

    if (this.reduced) {
      this.startActive(false);
    } else {
      root.classList.add('is-pre');
    }
    this.observe();
  }

  /* ---------- helpers for listeners so the editor can unload cleanly ---------- */
  Rail.prototype.on = function (el, type, fn, opts) {
    el.addEventListener(type, fn, opts || false);
    this.listeners.push([el, type, fn, opts || false]);
  };

  /* ---------- measuring and modes ---------- */
  Rail.prototype.measure = function () {
    var first = this.visible[0] || this.items[0];
    var H = first.garment ? first.garment.offsetHeight : 0;
    if (!H) H = parseFloat(getComputedStyle(this.root).getPropertyValue('--bmr-h')) || 440;
    var gap = parseFloat(getComputedStyle(this.root).getPropertyValue('--bmr-gap')) || 0;
    var W = H * 2 / 3;
    var wc = first.li.offsetWidth || (W * 0.44 + gap);
    var we = W * 0.94 + gap;
    this.m = { H: H, W: W, wc: wc, we: we, d: Math.max(0, (we - wc) / 2) };
  };

  Rail.prototype.setMode = function () {
    this.hoverMode = hoverMQ.matches;
    this.root.classList.toggle('is-hover-mode', this.hoverMode);
    this.layoutPadding();
  };

  Rail.prototype.layoutPadding = function () {
    var vw = this.viewport.clientWidth;
    var m = this.m;
    var pad = Math.round(m.d + 28);
    var natural = this.visible.length * m.wc + pad * 2;
    this.centerMode = !this.hoverMode && natural > vw;
    if (this.centerMode) pad = Math.max(pad, Math.round(vw / 2 - m.wc / 2));
    this.track.style.paddingLeft = pad + 'px';
    this.track.style.paddingRight = pad + 'px';
    this.root.classList.toggle('is-center-mode', this.centerMode);
    var overflow = this.track.offsetWidth > vw + 2;
    this.overflow = overflow;
    this.viewport.classList.toggle('has-overflow', overflow);
    if (this.viewport.parentNode) this.viewport.parentNode.classList.toggle('has-overflow', overflow);
    var showArrows = overflow && this.hoverMode;
    if (this.prevBtn) this.prevBtn.hidden = !showArrows;
    if (this.nextBtn) this.nextBtn.hidden = !showArrows;
    this.updateArrows();
  };

  Rail.prototype.updateArrows = function () {
    if (!this.prevBtn || this.prevBtn.hidden) return;
    var vi = this.visible.indexOf(this.active);
    this.prevBtn.disabled = vi <= 0;
    this.nextBtn.disabled = vi === -1 || vi >= this.visible.length - 1;
  };

  /* ---------- intro and visibility ---------- */
  Rail.prototype.observe = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) {
      this.inView = true;
      this.intro();
      return;
    }
    this.io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        self.inView = e.isIntersecting;
        if (e.isIntersecting) {
          if (!self.introDone) self.intro();
          self.kick();
        }
      });
    }, { threshold: 0.18 });
    this.io.observe(this.root);
  };

  Rail.prototype.intro = function () {
    var self = this;
    if (this.introDone) return;
    this.introDone = true;
    if (this.reduced) return;
    var list = this.visible;
    list.forEach(function (it, n) { it.li.style.transitionDelay = (n * 70) + 'ms'; });
    void this.track.offsetWidth;
    this.root.classList.remove('is-pre');
    list.forEach(function (it, n) {
      setTimeout(function () { it.omega -= 72 * SLIDE_KICK * 0.9; self.kick(); }, n * 70 + 380);
    });
    var total = list.length * 70 + 900;
    setTimeout(function () {
      list.forEach(function (it) { it.li.style.transitionDelay = ''; });
      self.startActive(true);
    }, total);
  };

  Rail.prototype.startActive = function (animate) {
    if (this.active) return;
    var n = Number(this.cfg.startItem) || 0;
    var it = n > 0 && this.items[n - 1] && this.items[n - 1].visible ? this.items[n - 1] : this.middle();
    if (!it) return;
    if (this.centerMode) this.scrollToItem(it, false);
    this.activate(it, { quiet: !animate });
  };

  Rail.prototype.middle = function () {
    var list = this.visible;
    if (!list.length) return null;
    if (this.overflow) {
      var c = this.viewport.scrollLeft + this.viewport.clientWidth / 2;
      return this.nearest(c);
    }
    return list[Math.floor((list.length - 1) / 2)];
  };

  Rail.prototype.nearest = function (center) {
    var best = null, bd = Infinity, half = this.m.wc / 2;
    this.visible.forEach(function (it) {
      var d = Math.abs(it.li.offsetLeft + half - center);
      if (d < bd) { bd = d; best = it; }
    });
    return best;
  };

  /* ---------- activation (turn to front) ---------- */
  Rail.prototype.activate = function (it, opts) {
    opts = opts || {};
    if (!it || it === this.active) return;
    var prev = this.active;
    if (prev) prev.li.classList.remove('is-active');
    this.active = it;
    it.li.classList.add('is-active');
    this.applyShift(!opts.quiet);
    if (!opts.quiet && !this.reduced) {
      var dir = prev ? (prev.i < it.i ? -1 : 1) : 1;
      it.omega += dir * 6;
    }
    this.updateInfo(it, !!opts.quiet);
    this.updateArrows();
    this.roving(it);
    this.kick();
  };

  Rail.prototype.applyShift = function (kick) {
    var a = this.visible.indexOf(this.active);
    var d = this.m.d;
    var self = this;
    this.visible.forEach(function (it, vi) {
      var x = a === -1 ? 0 : vi < a ? -d : vi > a ? d : 0;
      if (kick && !self.reduced && x !== it.x) it.omega += (x - it.x) * SLIDE_KICK;
      it.x = x;
      it.slide.style.setProperty('--x', x.toFixed(1) + 'px');
      it.li.classList.toggle('is-near', a !== -1 && Math.abs(vi - a) === 1);
    });
  };

  Rail.prototype.roving = function (it) {
    this.items.forEach(function (x) { x.hanger.setAttribute('tabindex', x === it ? '0' : '-1'); });
  };

  Rail.prototype.updateInfo = function (it, instant) {
    var self = this;
    var d = it.data;
    var t = this.text;
    clearTimeout(this.infoTimer);
    var fill = function () {
      self.infoEyebrow.textContent = [d.group, d.badge].filter(Boolean).join(' · ');
      self.infoTitle.textContent = d.title || '';
      var meta = [];
      if (d.colour) meta.push(esc(d.colour));
      if (self.cfg.showPrice && d.price) meta.push('<strong>' + esc(d.price) + '</strong>' + (d.compare ? '<s>' + esc(d.compare) + '</s>' : ''));
      self.infoMeta.innerHTML = meta.join(' · ');
      if (self.openBtn) self.openBtn.setAttribute('aria-label', (self.openBtn.textContent || '').trim() + ': ' + d.title + (d.colour ? ', ' + d.colour : ''));
      self.infoBody.classList.remove('is-out');
    };
    if (instant || this.reduced) { fill(); return; }
    this.infoBody.classList.add('is-out');
    this.infoTimer = setTimeout(fill, 170);
  };

  /* ---------- the pendulum loop ---------- */
  Rail.prototype.kick = function () {
    if (this.running || this.reduced || !this.inView || document.hidden) return;
    var self = this;
    this.running = true;
    this.lastTime = 0;
    requestAnimationFrame(function (t) { self.tick(t); });
  };

  Rail.prototype.tick = function (now) {
    var self = this;
    if (!this.inView || document.hidden || this.destroyed) { this.running = false; return; }
    var dt = this.lastTime ? Math.min(0.034, (now - this.lastTime) / 1000) : 0.016;
    this.lastTime = now;
    var t = now / 1000;
    var amp = this.paused ? 0 : this.sway * 0.2;
    var moving = false;

    // The rail moved: garments lag behind, then swing back.
    var sl = this.viewport.scrollLeft;
    var ds = sl - this.lastScroll;
    this.lastScroll = sl;
    if (ds && !this.paused) {
      var push = clamp(-ds * SCROLL_KICK, -30, 30);
      this.visible.forEach(function (it) { it.omega += push; });
    }

    for (var n = 0; n < this.visible.length; n++) {
      var it = this.visible[n];
      var target = amp ? amp * (Math.sin(t * 0.85 + it.phase) * 0.65 + Math.sin(t * 0.47 + it.phase * 1.9) * 0.35) : 0;
      var acc = -K * (it.theta - target) - C * it.omega;
      it.omega += acc * dt;
      it.theta = clamp(it.theta + it.omega * dt, -MAX_ANGLE, MAX_ANGLE);
      if (Math.abs(it.omega) > 0.03 || Math.abs(it.theta - target) > 0.03) moving = true;
      if (it.written === null || Math.abs(it.theta - it.written) > 0.004) {
        it.written = it.theta;
        it.garment.style.transform = 'rotate(' + it.theta.toFixed(3) + 'deg)';
      }
    }

    if (amp || moving || ds) {
      requestAnimationFrame(function (tt) { self.tick(tt); });
    } else {
      this.running = false;
    }
  };

  /* ---------- phones: the garment in the middle turns ---------- */
  Rail.prototype.centerCheck = function () {
    if (this.filtering) return;
    var c = this.viewport.scrollLeft + this.viewport.clientWidth / 2;
    var it = this.nearest(c);
    if (!it || it === this.active) return;
    var half = this.m.wc / 2;
    var dNew = Math.abs(it.li.offsetLeft + half - c);
    var dOld = this.active ? Math.abs(this.active.li.offsetLeft + half - c) : Infinity;
    if (dNew + 6 < dOld) this.activate(it);
  };

  Rail.prototype.scrollToItem = function (it, smoothScroll) {
    var left = it.li.offsetLeft + this.m.wc / 2 - this.viewport.clientWidth / 2;
    left = clamp(left, 0, this.viewport.scrollWidth - this.viewport.clientWidth);
    if (Math.abs(left - this.viewport.scrollLeft) < 2) return;
    try {
      this.viewport.scrollTo({ left: left, behavior: smoothScroll && !this.reduced ? 'smooth' : 'auto' });
    } catch (e) {
      this.viewport.scrollLeft = left;
    }
  };

  Rail.prototype.ensureVisible = function (it, smoothScroll) {
    if (!this.overflow) return;
    var vp = this.viewport;
    var left = it.li.offsetLeft - this.m.d - 40;
    var right = it.li.offsetLeft + this.m.wc + this.m.d + 40;
    if (left < vp.scrollLeft || right > vp.scrollLeft + vp.clientWidth) this.scrollToItem(it, smoothScroll);
  };

  /* ---------- events ---------- */
  Rail.prototype.itemFrom = function (el) {
    var li = el && el.closest ? el.closest('[data-bmr-item]') : null;
    if (!li) return null;
    for (var n = 0; n < this.items.length; n++) if (this.items[n].li === li) return this.items[n];
    return null;
  };

  Rail.prototype.bind = function () {
    var self = this;
    var vp = this.viewport;

    // Hover turns (desktop), with a short intent delay so sweeping across the rail stays calm.
    this.on(this.track, 'pointerover', function (e) {
      if (e.pointerType !== 'mouse' || !self.hoverMode || !self.cfg.hoverTurn || self.drag) return;
      var it = self.itemFrom(e.target);
      if (!it || !it.visible) return;
      self.hoverItem = it;
      clearTimeout(self.hoverTimer);
      self.hoverTimer = setTimeout(function () {
        if (self.hoverItem === it && !self.drag) self.activate(it);
      }, 90);
    });
    this.on(this.track, 'pointerleave', function () { self.hoverItem = null; clearTimeout(self.hoverTimer); });

    // Brushing past garments nudges them.
    var lastX = null, lastT = 0;
    this.on(this.track, 'pointermove', function (e) {
      if (e.pointerType !== 'mouse' || self.reduced) return;
      var now = performance.now();
      if (lastX !== null && now > lastT) {
        var vx = (e.clientX - lastX) / (now - lastT);
        var it = self.itemFrom(e.target);
        if (it && Math.abs(vx) > 0.15) { it.omega -= clamp(vx * BRUSH_KICK, -14, 14); self.kick(); }
      }
      lastX = e.clientX; lastT = now;
    });

    // Drag the rail with a mouse; garments swing from the movement.
    this.on(vp, 'pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0 || !self.overflow) return;
      self.stopGlide();
      self.drag = { x: e.clientX, left: vp.scrollLeft, moved: false, id: e.pointerId, v: 0, lx: e.clientX, lt: performance.now() };
      self.dragMoved = false;
    });
    this.on(window, 'pointermove', function (e) {
      var d = self.drag;
      if (!d || e.pointerId !== d.id) return;
      var dx = e.clientX - d.x;
      if (!d.moved && Math.abs(dx) > 6) {
        d.moved = true;
        self.dragMoved = true;
        self.root.classList.add('is-dragging');
        clearTimeout(self.hoverTimer);
      }
      if (d.moved) {
        vp.scrollLeft = d.left - dx;
        var now = performance.now();
        if (now > d.lt) d.v = (e.clientX - d.lx) / (now - d.lt);
        d.lx = e.clientX; d.lt = now;
        self.kick();
      }
    });
    var endDrag = function (e) {
      var d = self.drag;
      if (!d || (e.pointerId !== undefined && e.pointerId !== d.id)) return;
      self.drag = null;
      self.root.classList.remove('is-dragging');
      if (d.moved) self.glide(-d.v * 16);
    };
    this.on(window, 'pointerup', endDrag);
    this.on(window, 'pointercancel', endDrag);

    // Clicks and taps.
    this.on(this.track, 'click', function (e) {
      var it = self.itemFrom(e.target);
      if (!it) return;
      if (self.dragMoved) { e.preventDefault(); self.dragMoved = false; return; }
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button > 0) return;
      var isActive = it === self.active;
      if (!isActive && (!self.hoverMode || !self.cfg.hoverTurn)) {
        e.preventDefault();
        if (self.centerMode) self.scrollToItem(it, true);
        self.activate(it);
        if (self.hoverMode) self.ensureVisible(it, true);
        return;
      }
      if (self.cfg.onClick === 'product') return;
      e.preventDefault();
      self.activate(it);
      self.open(it);
    });

    if (this.openBtn) {
      this.on(this.openBtn, 'click', function () {
        var it = self.active || self.middle();
        if (!it) return;
        if (self.cfg.onClick === 'product' && it.data.url) { window.location.href = it.data.url; return; }
        self.activate(it, { quiet: true });
        self.open(it);
      });
    }

    // Keyboard: one tab stop on the rail, arrows move between garments.
    this.on(this.track, 'pointerdown', function () {
      self.pointerFocus = true;
      clearTimeout(self.pointerFocusTimer);
      self.pointerFocusTimer = setTimeout(function () { self.pointerFocus = false; }, 700);
    });
    this.on(this.track, 'focusin', function (e) {
      if (self.pointerFocus) return;
      var it = self.itemFrom(e.target);
      if (!it || it === self.active) return;
      self.activate(it);
      if (self.centerMode) self.scrollToItem(it, true); else self.ensureVisible(it, true);
    });
    this.on(this.track, 'keydown', function (e) {
      var it = self.itemFrom(e.target);
      if (!it) return;
      var vi = self.visible.indexOf(it), next = null;
      if (e.key === 'ArrowRight') next = self.visible[vi + 1];
      else if (e.key === 'ArrowLeft') next = self.visible[vi - 1];
      else if (e.key === 'Home') next = self.visible[0];
      else if (e.key === 'End') next = self.visible[self.visible.length - 1];
      else return;
      e.preventDefault();
      if (next) next.hanger.focus({ preventScroll: true });
    });

    // Arrows.
    var step = function (dir) {
      var vi = self.visible.indexOf(self.active);
      var it = self.visible[clamp(vi + dir, 0, self.visible.length - 1)];
      if (!it || it === self.active) return;
      self.activate(it);
      self.scrollToItem(it, true);
    };
    if (this.prevBtn) this.on(this.prevBtn, 'click', function () { step(-1); });
    if (this.nextBtn) this.on(this.nextBtn, 'click', function () { step(1); });

    // Native scrolling (touch, trackpad) feeds the pendulums; on phones the middle garment turns.
    this.on(vp, 'scroll', function () {
      self.kick();
      if (!self.centerMode || self.scrollRaf) return;
      self.scrollRaf = requestAnimationFrame(function () { self.scrollRaf = 0; self.centerCheck(); });
    }, { passive: true });

    // Tabs.
    if (this.tabsEl) {
      this.on(this.tabsEl, 'click', function (e) {
        var b = e.target.closest('[data-bmr-tab]');
        if (b) self.setFilter(b.getAttribute('data-bmr-tab'));
      });
    }

    // Layout changes.
    var onResize = function () {
      cancelAnimationFrame(self.resizeRaf);
      self.resizeRaf = requestAnimationFrame(function () {
        self.measure();
        self.setMode();
        self.applyShift(false);
        self.moveTabLine(false);
      });
    };
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(onResize);
      this.ro.observe(this.viewport);
    } else {
      this.on(window, 'resize', onResize);
    }
    var onMode = function () { onResize(); };
    if (hoverMQ.addEventListener) { hoverMQ.addEventListener('change', onMode); this.mqListener = onMode; }
    this.on(document, 'visibilitychange', function () { self.kick(); });
  };

  // Mouse drag inertia.
  Rail.prototype.glide = function (v) {
    var self = this;
    var vp = this.viewport;
    this.stopGlide();
    if (Math.abs(v) < 0.5 || this.reduced) return;
    var step = function () {
      v *= 0.93;
      vp.scrollLeft += v;
      self.kick();
      if (Math.abs(v) > 0.4) self.glideRaf = requestAnimationFrame(step);
    };
    this.glideRaf = requestAnimationFrame(step);
  };
  Rail.prototype.stopGlide = function () { cancelAnimationFrame(this.glideRaf); };

  /* ---------- filter tabs ---------- */
  Rail.prototype.updateTabs = function (animate) {
    if (!this.tabsEl) return;
    var key = this.filter;
    [].forEach.call(this.tabsEl.querySelectorAll('[data-bmr-tab]'), function (b) {
      var on = b.getAttribute('data-bmr-tab') === key;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    this.moveTabLine(animate);
  };

  Rail.prototype.moveTabLine = function (animate) {
    if (!this.tabLine || !this.tabsEl) return;
    var b = this.tabsEl.querySelector('[data-bmr-tab].is-active');
    if (!b) return;
    if (!animate) this.tabLine.style.transition = 'none';
    this.tabLine.style.width = b.offsetWidth + 'px';
    this.tabLine.style.transform = 'translate3d(' + b.offsetLeft + 'px,' + (b.offsetTop + b.offsetHeight - 3) + 'px,0)';
    if (!animate) { void this.tabLine.offsetWidth; this.tabLine.style.transition = ''; }
  };

  Rail.prototype.setFilter = function (key) {
    var self = this;
    if (key === this.filter || this.filtering) return;
    this.filter = key;
    this.updateTabs(true);
    var match = function (it) { return key === 'all' || it.group === key; };
    var leaving = this.items.filter(function (it) { return it.visible && !match(it); });
    var entering = this.items.filter(function (it) { return !it.visible && match(it); });
    var staying = this.items.filter(function (it) { return it.visible && match(it); });
    if (!leaving.length && !entering.length) return;
    this.filtering = true;

    var first = staying.map(function (it) { return it.slide.getBoundingClientRect().left; });
    leaving.forEach(function (it) { it.li.classList.add('is-leaving'); });

    setTimeout(function () {
      leaving.forEach(function (it) {
        it.li.hidden = true;
        it.li.classList.remove('is-leaving', 'is-active', 'is-near');
        it.visible = false;
      });
      entering.forEach(function (it, n) {
        it.li.hidden = false;
        it.visible = true;
        it.x = 0;
        it.theta = 0; it.omega = 0; it.written = null;
        it.garment.style.transform = '';
        it.li.classList.add('is-entering');
      });
      self.visible = self.items.filter(function (it) { return it.visible; });
      if (self.active && !self.active.visible) self.active = null;

      self.measure();
      self.layoutPadding();
      var keep = self.active || self.middle();
      if (self.active !== keep) {
        if (self.active) self.active.li.classList.remove('is-active');
        self.active = keep;
        if (keep) keep.li.classList.add('is-active');
        if (keep) self.updateInfo(keep, false);
      }
      if (keep && self.overflow) {
        var target = keep.li.offsetLeft + self.m.wc / 2 - self.viewport.clientWidth / 2;
        self.viewport.scrollLeft = clamp(target, 0, self.viewport.scrollWidth - self.viewport.clientWidth);
      } else {
        self.viewport.scrollLeft = 0;
      }
      self.lastScroll = self.viewport.scrollLeft;

      // FLIP: garments that stay slide along the rail to their new place.
      staying.forEach(function (it) { it.slide.style.transition = 'none'; });
      self.applyShift(false);
      var last = staying.map(function (it) { return it.slide.getBoundingClientRect().left; });
      staying.forEach(function (it, n) {
        var dx = first[n] - last[n];
        it.flipDx = dx;
        it.slide.style.setProperty('--x', (it.x + dx).toFixed(1) + 'px');
      });
      void self.track.offsetWidth;
      staying.forEach(function (it) {
        it.slide.style.transition = '';
        it.slide.style.setProperty('--x', it.x.toFixed(1) + 'px');
        if (!self.reduced) it.omega += clamp(-it.flipDx * SLIDE_KICK * 0.8, -22, 22);
      });

      // New garments drop onto the rail one by one.
      entering.forEach(function (it, n) {
        setTimeout(function () {
          it.li.classList.remove('is-entering');
          if (!self.reduced) it.omega += (n % 2 ? 1 : -1) * 9;
          self.kick();
        }, 80 + n * 80);
      });

      self.updateArrows();
      self.roving(self.active);
      self.kick();
      setTimeout(function () { self.filtering = false; }, 700 + entering.length * 80);
    }, leaving.length ? 330 : 0);
  };

  /* ---------- details panel ---------- */
  Rail.prototype.buildPanel = function () {
    var t = this.text;
    var p = document.createElement('div');
    p.className = 'bmr-panel';
    p.hidden = true;
    p.setAttribute('role', 'dialog');
    p.setAttribute('aria-modal', 'true');
    p.setAttribute('aria-labelledby', this.id + '-title');
    p.setAttribute('style', this.root.getAttribute('style') || '');
    var cartAdd = this.cfg.cartRoot || '/cart/add';
    p.innerHTML =
      '<div class="bmr-panel__backdrop"></div>' +
      '<button type="button" class="bmr-panel__close" data-bmr-close aria-label="' + esc(t.close || 'Close') + '">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.3"/></svg></button>' +
      '<div class="bmr-panel__sheet" data-bmr-sheet>' +
        '<div class="bmr-panel__stage">' +
          '<div class="bmr-panel__halo" aria-hidden="true"></div>' +
          '<div class="bmr-panel__turn" data-bmr-turn tabindex="0" role="img">' +
            '<img data-view="front" alt="" draggable="false" decoding="async">' +
            '<img data-view="side" alt="" draggable="false" decoding="async">' +
            '<img data-view="back" alt="" draggable="false" decoding="async">' +
          '</div>' +
          '<div class="bmr-panel__controls">' +
            '<div class="bmr-panel__views" role="group" aria-label="' + esc(t.views || 'View') + '" data-bmr-views></div>' +
            '<p class="bmr-panel__drag" data-bmr-drag><svg viewBox="0 0 22 12" aria-hidden="true"><path d="M5 2 1 6l4 4M17 2l4 4-4 4M1 6h20" fill="none" stroke="currentColor" stroke-width="1"/></svg><span>' + esc(t.drag || 'Drag to turn') + '</span></p>' +
          '</div>' +
        '</div>' +
        '<div class="bmr-panel__info">' +
          '<p class="bmr-panel__eyebrow" data-p-eyebrow></p>' +
          '<h2 class="bmr-panel__title" id="' + this.id + '-title" data-p-title></h2>' +
          '<p class="bmr-panel__price" data-p-price></p>' +
          '<div data-p-swatch-wrap><p class="bmr-panel__swatch-label" data-p-swatch-label></p><div class="bmr-panel__swatches" role="radiogroup" data-p-swatches></div></div>' +
          '<p class="bmr-panel__desc" data-p-desc></p>' +
          '<form class="bmr-panel__form" method="post" action="' + esc(cartAdd) + '" accept-charset="UTF-8" data-p-form>' +
            '<input type="hidden" name="form_type" value="product">' +
            '<input type="hidden" name="utf8" value="✓">' +
            '<input type="hidden" name="id" value="" data-p-variant>' +
            '<input type="hidden" name="quantity" value="1">' +
            '<input type="hidden" name="properties[_From]" value="Clothes rail">' +
            '<button type="submit" class="bmr__btn bmr__btn--solid" data-add-to-cart data-p-add><span data-p-add-label></span></button>' +
          '</form>' +
          '<a class="bmr-panel__link" href="#" data-p-link>' + esc(t.viewProduct || 'View full details') + '</a>' +
        '</div>' +
      '</div>';
    document.body.appendChild(p);
    this.panel = p;
    this.pTurn = p.querySelector('[data-bmr-turn]');
    this.pImgs = {};
    var self = this;
    [].forEach.call(this.pTurn.querySelectorAll('img'), function (img) { self.pImgs[img.getAttribute('data-view')] = img; });
    this.pViews = p.querySelector('[data-bmr-views]');
    this.pDrag = p.querySelector('[data-bmr-drag]');
    this.pClose = p.querySelector('.bmr-panel__close');
    this.pSheet = p.querySelector('[data-bmr-sheet]');

    this.on(p, 'click', function (e) {
      var outside = e.target === self.pSheet || e.target.classList.contains('bmr-panel__stage');
      if (e.target.closest('[data-bmr-close]') || (outside && performance.now() - (self.turnEnded || 0) > 350)) self.close();
      var sw = e.target.closest('[data-bmr-swatch]');
      if (sw) self.switchTo(self.items[Number(sw.getAttribute('data-bmr-swatch'))]);
      var vb = e.target.closest('[data-bmr-view]');
      if (vb) { self.tweenAngle(Number(vb.getAttribute('data-bmr-view')) * 90, 620); self.hideDragHint(); }
    });
    this.on(p, 'keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); self.close(); return; }
      if (e.key === 'Tab') self.trap(e);
      if (e.target === self.pTurn && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        var idx = Math.round(self.angle / 90) + (e.key === 'ArrowLeft' ? 1 : -1);
        self.tweenAngle(clamp(idx, 0, self.pViewList.length - 1) * 90, 620);
        self.hideDragHint();
      }
      var sw = e.target.closest('[data-bmr-swatch]');
      if (sw && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
        e.preventDefault();
        var sws = [].slice.call(self.panel.querySelectorAll('[data-bmr-swatch]'));
        var k = sws.indexOf(sw) + (e.key === 'ArrowRight' ? 1 : -1);
        var target = sws[(k + sws.length) % sws.length];
        target.focus();
        target.click();
      }
    });
    var add = p.querySelector('[data-p-add]');
    this.on(add, 'click', function () {
      if (add.disabled) return;
      // The theme adds to the bag and opens its cart drawer; step aside for it.
      // Without the theme script the form simply posts to the cart page.
      if (window.theme) setTimeout(function () { self.close(true); }, 60);
    });

    // Drag to turn.
    var drag = null;
    this.on(this.pTurn, 'pointerdown', function (e) {
      if (self.pViewList.length < 2 || (e.pointerType === 'mouse' && e.button !== 0)) return;
      self.stopTween();
      drag = { x: e.clientX, y: e.clientY, a: self.angle, id: e.pointerId, v: 0, lx: e.clientX, lt: performance.now(), locked: false };
    });
    this.on(this.pTurn, 'pointermove', function (e) {
      if (!drag || e.pointerId !== drag.id) return;
      var dx = e.clientX - drag.x, dy = e.clientY - drag.y;
      if (!drag.locked) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) { drag = null; return; }
        drag.locked = true;
        try { self.pTurn.setPointerCapture(e.pointerId); } catch (err) {}
        self.pTurn.classList.add('is-grabbing');
        self.hideDragHint();
      }
      var max = (self.pViewList.length - 1) * 90;
      var a = drag.a - dx * (90 / Math.max(160, self.pTurn.offsetWidth * 0.55));
      if (a < 0) a = a * 0.3;
      if (a > max) a = max + (a - max) * 0.3;
      self.renderAngle(a);
      var now = performance.now();
      if (now > drag.lt) drag.v = (e.clientX - drag.lx) / (now - drag.lt);
      drag.lx = e.clientX; drag.lt = now;
    });
    var endTurn = function (e) {
      if (!drag || (e.pointerId !== undefined && e.pointerId !== drag.id)) return;
      var v = drag.v;
      var locked = drag.locked;
      drag = null;
      if (locked) self.turnEnded = performance.now();
      self.pTurn.classList.remove('is-grabbing');
      if (!locked) return;
      var max = (self.pViewList.length - 1) * 90;
      var projected = self.angle - v * 140;
      self.tweenAngle(clamp(Math.round(projected / 90) * 90, 0, max), 560);
    };
    this.on(this.pTurn, 'pointerup', endTurn);
    this.on(this.pTurn, 'pointercancel', endTurn);
  };

  Rail.prototype.trap = function (e) {
    var f = [].filter.call(this.panel.querySelectorAll('button, a[href], [tabindex="0"], input:not([type="hidden"])'), function (el) {
      return !el.disabled && el.offsetParent !== null;
    });
    if (!f.length) return;
    var firstEl = f[0], lastEl = f[f.length - 1];
    if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
    else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
  };

  Rail.prototype.variantFor = function (d) {
    var vs = d.variants || [];
    var c = String(d.colour || '').toLowerCase();
    var v = null;
    if (c) v = vs.filter(function (x) { return (x.options || []).some(function (o) { return String(o).toLowerCase() === c; }); })[0];
    return v || vs.filter(function (x) { return x.available; })[0] || vs[0] || null;
  };

  Rail.prototype.fillPanel = function (it) {
    var self = this;
    var p = this.panel;
    var d = it.data;
    var t = this.text;
    var imgs = d.images || {};
    var front = imgs.front || imgs.side;
    this.pViewList = ['front'];
    if (imgs.side && d.realSide !== false) this.pViewList.push('side');
    if (imgs.back) this.pViewList.push('back');

    ['front', 'side', 'back'].forEach(function (v) {
      var img = self.pImgs[v];
      var src = v === 'front' ? front : imgs[v];
      var on = self.pViewList.indexOf(v) > -1 && src;
      img.hidden = !on;
      if (!on) { img.removeAttribute('src'); img.removeAttribute('srcset'); return; }
      img.sizes = '(max-width: 899px) 60vw, 520px';
      if (src.srcset) img.srcset = src.srcset;
      img.src = resize(src.src, 900);
    });
    this.pTurn.setAttribute('aria-label', d.title + (d.colour ? ', ' + d.colour : '') + (this.pViewList.length > 1 ? '. ' + (t.drag || '') : ''));
    this.pTurn.classList.toggle('is-single', this.pViewList.length < 2);

    this.pViews.hidden = this.pViewList.length < 2;
    this.pViews.innerHTML = this.pViewList.map(function (v, n) {
      return '<button type="button" class="bmr-panel__view" data-bmr-view="' + n + '" aria-pressed="false">' + esc(t[v] || v) + '</button>';
    }).join('');
    this.pDrag.hidden = this.pViewList.length < 2;

    p.querySelector('[data-p-eyebrow]').textContent = [d.group, d.badge].filter(Boolean).join(' · ');
    p.querySelector('[data-p-title]').textContent = d.title || '';
    var v = this.variantFor(d);
    var price = (v && v.price) || d.price;
    p.querySelector('[data-p-price]').innerHTML = this.cfg.showPrice && price ? esc(price) + (d.compare ? '<s>' + esc(d.compare) + '</s>' : '') : '';
    p.querySelector('[data-p-desc]').textContent = d.description || '';

    // Colour swatches: garments of the same product.
    var sibs = d.handle ? this.items.filter(function (x) { return x.data.handle === d.handle && x.data.colour; }) : [];
    var wrap = p.querySelector('[data-p-swatch-wrap]');
    wrap.hidden = sibs.length < 2 && !d.colour;
    p.querySelector('[data-p-swatch-label]').innerHTML = d.colour ? esc(t.colour || 'Colour') + ': <strong>' + esc(d.colour) + '</strong>' : '';
    var sw = p.querySelector('[data-p-swatches]');
    sw.setAttribute('aria-label', t.colour || 'Colour');
    sw.innerHTML = sibs.length < 2 ? '' : sibs.map(function (x) {
      var f = x.data.images.front || x.data.images.side || {};
      var on = x === it;
      return '<button type="button" class="bmr-panel__swatch" role="radio" aria-checked="' + on + '" tabindex="' + (on ? '0' : '-1') + '" data-bmr-swatch="' + x.i + '" aria-label="' + esc(x.data.colour) + '">' +
        (f.src ? '<img src="' + esc(resize(f.src, 180)) + '" alt="" loading="lazy">' : '') + '</button>';
    }).join('');

    var add = p.querySelector('[data-p-add]');
    var available = v ? v.available : d.available;
    p.querySelector('[data-p-variant]').value = v ? v.id : '';
    add.disabled = !v || !available;
    p.querySelector('[data-p-add-label]').textContent = available && v ? (t.add || 'Add to bag') + (this.cfg.showPrice && price ? ' · ' + price : '') : (t.soldOut || 'Sold out');
    var link = p.querySelector('[data-p-link]');
    link.href = d.url || '#';
    link.hidden = !d.url;
  };

  Rail.prototype.renderAngle = function (a) {
    this.angle = a;
    var self = this;
    var list = this.pViewList;
    list.forEach(function (v, n) {
      var img = self.pImgs[v];
      var delta = a - n * 90;
      var w = clamp(1 - Math.abs(delta) / 90, 0, 1);
      var e = smooth(clamp((w - 0.3) / 0.4, 0, 1));
      img.style.opacity = e.toFixed(3);
      img.style.transform = self.reduced ? '' : 'perspective(1400px) rotateY(' + (-delta * 0.32).toFixed(2) + 'deg) scaleX(' + (0.62 + 0.38 * smooth(w)).toFixed(3) + ')';
    });
    var near = Math.round(clamp(a, 0, (list.length - 1) * 90) / 90);
    [].forEach.call(this.pViews.querySelectorAll('[data-bmr-view]'), function (b, n) { b.setAttribute('aria-pressed', n === near ? 'true' : 'false'); });
  };

  Rail.prototype.tweenAngle = function (to, ms) {
    var self = this;
    this.stopTween();
    var from = this.angle || 0;
    if (this.reduced || Math.abs(to - from) < 0.5) { this.renderAngle(to); return; }
    var start = performance.now();
    var step = function (now) {
      var k = clamp((now - start) / ms, 0, 1);
      self.renderAngle(from + (to - from) * easeOutCubic(k));
      if (k < 1) self.tweenRaf = requestAnimationFrame(step);
    };
    this.tweenRaf = requestAnimationFrame(step);
  };
  Rail.prototype.stopTween = function () { cancelAnimationFrame(this.tweenRaf); };

  Rail.prototype.hideDragHint = function () { if (this.pDrag) this.pDrag.classList.add('is-done'); };

  // A gentle half-turn after opening, so people see it can be turned.
  Rail.prototype.peek = function () {
    var self = this;
    if (this.reduced || this.pViewList.length < 2 || this.peeked) return;
    this.peeked = true;
    this.tweenAngle(26, 700);
    setTimeout(function () { if (self.panelOpen) self.tweenAngle(0, 900); }, 760);
  };

  Rail.prototype.switchTo = function (it) {
    var self = this;
    if (!it || it === this.panelItem) return;
    this.panelItem = it;
    var keepView = Math.round((this.angle || 0) / 90);
    if (it.visible) this.activate(it, { quiet: true });
    if (this.reduced) { this.fillPanel(it); this.renderAngle(0); this.focusSwatch(); return; }
    this.pTurn.classList.add('is-swapping');
    setTimeout(function () {
      self.fillPanel(it);
      self.renderAngle(clamp(keepView, 0, self.pViewList.length - 1) * 90);
      self.focusSwatch();
      requestAnimationFrame(function () { self.pTurn.classList.remove('is-swapping'); });
    }, 200);
  };

  Rail.prototype.focusSwatch = function () {
    var on = this.panel.querySelector('[data-bmr-swatch][aria-checked="true"]');
    if (on && this.panel.contains(document.activeElement) && document.activeElement.hasAttribute('data-bmr-swatch')) on.focus();
  };

  Rail.prototype.lock = function (on) {
    var html = document.documentElement;
    if (on) {
      var sb = window.innerWidth - html.clientWidth;
      html.classList.add('bmr-lock');
      if (sb > 0) html.style.paddingRight = sb + 'px';
    } else {
      html.classList.remove('bmr-lock');
      html.style.paddingRight = '';
    }
  };

  // Moves an image from one place to another with a floating copy.
  Rail.prototype.fly = function (fromEl, toEl, src, ms, done) {
    var a = fromEl.getBoundingClientRect();
    var b = toEl.getBoundingClientRect();
    if (!a.width || !b.width || !src || this.reduced || !document.body.animate) { done && done(); return; }
    var fl = document.createElement('img');
    fl.className = 'bmr-flyer';
    fl.alt = '';
    fl.src = src;
    fl.style.left = b.left + 'px';
    fl.style.top = b.top + 'px';
    fl.style.width = b.width + 'px';
    fl.style.height = b.height + 'px';
    document.body.appendChild(fl);
    var sx = a.width / b.width, sy = a.height / b.height;
    var anim = fl.animate([
      { transform: 'translate(' + (a.left - b.left) + 'px,' + (a.top - b.top) + 'px) scale(' + sx + ',' + sy + ')' },
      { transform: 'translate(0,0) scale(1,1)' }
    ], { duration: ms, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'forwards' });
    var finished = false;
    var finish = function () {
      if (finished) return;
      finished = true;
      done && done();
      requestAnimationFrame(function () { if (fl.parentNode) fl.parentNode.removeChild(fl); });
    };
    anim.onfinish = finish;
    setTimeout(finish, ms + 300);
  };

  Rail.prototype.open = function (it) {
    var self = this;
    if (!it || this.panelOpen) return;
    if (!this.panel) this.buildPanel();
    this.panelOpen = true;
    this.panelItem = it;
    this.lastFocus = document.activeElement;
    this.peeked = false;
    this.paused = true;
    this.fillPanel(it);
    this.renderAngle(0);
    this.pDrag.classList.remove('is-done');
    this.panel.classList.remove('is-closing');
    this.panel.hidden = false;
    this.lock(true);

    var railImg = it.front;
    var target = this.pImgs.front;
    var src = railImg && (railImg.currentSrc || railImg.src);
    var canFly = !this.reduced && railImg && railImg.complete && railImg.naturalWidth > 0;
    if (canFly) { target.style.visibility = 'hidden'; railImg.style.visibility = 'hidden'; }

    requestAnimationFrame(function () {
      self.panel.classList.add('is-open');
      try { self.pClose.focus({ preventScroll: true }); } catch (e) { self.pClose.focus(); }
      if (canFly) {
        self.fly(railImg, target, src, 860, function () {
          target.style.visibility = '';
          railImg.style.visibility = '';
          setTimeout(function () { self.peek(); }, 250);
        });
      } else {
        setTimeout(function () { self.peek(); }, 500);
      }
    });
  };

  Rail.prototype.close = function (forCart) {
    var self = this;
    if (!this.panelOpen) return;
    this.panelOpen = false;
    this.stopTween();
    var it = this.panelItem;
    var p = this.panel;

    // Fly back from the view that is showing now onto the rail.
    var shown = this.pViewList[Math.round(clamp(this.angle || 0, 0, (this.pViewList.length - 1) * 90) / 90)] || 'front';
    var fromImg = this.pImgs[shown];
    var railImg = it && it.visible ? it.front : null;
    if (railImg && this.overflow) this.ensureVisible(it, false);
    var canFly = !forCart && !this.reduced && railImg && fromImg && fromImg.complete && fromImg.naturalWidth > 0;

    p.classList.add('is-closing');
    p.classList.remove('is-open');

    var finish = function () {
      p.hidden = true;
      p.classList.remove('is-closing');
      self.lock(false);
      self.paused = false;
      self.kick();
      if (railImg) railImg.style.visibility = '';
      [fromImg].forEach(function (img) { if (img) img.style.visibility = ''; });
      if (!forCart && self.lastFocus && document.contains(self.lastFocus)) {
        var back = it && it.visible ? it.hanger : self.lastFocus;
        try { back.focus({ preventScroll: true }); } catch (e) {}
      }
    };

    if (canFly) {
      fromImg.style.visibility = 'hidden';
      railImg.style.visibility = 'hidden';
      this.fly(fromImg, railImg, fromImg.currentSrc || fromImg.src, 780, finish);
    } else {
      setTimeout(finish, forCart ? 280 : 420);
    }
  };

  /* ---------- theme editor ---------- */
  Rail.prototype.selectBlock = function (blockId) {
    var it = null;
    this.items.forEach(function (x) { if (x.blockId === blockId) it = x; });
    if (!it) return;
    var self = this;
    var go = function () {
      self.activate(it);
      if (self.overflow) self.scrollToItem(it, true);
    };
    if (!it.visible) { this.setFilter('all'); setTimeout(go, 800); } else go();
  };

  Rail.prototype.destroy = function () {
    this.destroyed = true;
    this.listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
    this.listeners = [];
    if (this.io) this.io.disconnect();
    if (this.ro) this.ro.disconnect();
    if (this.mqListener && hoverMQ.removeEventListener) hoverMQ.removeEventListener('change', this.mqListener);
    this.stopGlide();
    this.stopTween();
    if (this.panel && this.panel.parentNode) this.panel.parentNode.removeChild(this.panel);
    this.lock(false);
  };

  /* ---------- boot ---------- */
  function init(scope) {
    [].forEach.call((scope || document).querySelectorAll('[data-bmr]'), function (el) {
      if (el.__bmr) return;
      el.__bmr = new Rail(el);
    });
  }

  window.BisouClothesRail = { init: init, Rail: Rail };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    [].forEach.call(e.target.querySelectorAll('[data-bmr]'), function (el) {
      if (el.__bmr && el.__bmr.destroy) el.__bmr.destroy();
      el.__bmr = null;
    });
  });
  document.addEventListener('shopify:block:select', function (e) {
    var el = e.target.closest && e.target.closest('[data-bmr]');
    if (el && el.__bmr && el.__bmr.selectBlock) el.__bmr.selectBlock(e.detail && e.detail.blockId);
  });
})();